<script setup>
import { computed, ref } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from './provider.js';
import { hasOneDayPassed } from 'shared/helpers/timeHelper';
import { ORIENTATION, MESSAGE_STATUS } from './constants';

defineProps({
  error: { type: String, required: true },
});

const emit = defineEmits(['retry']);

const { orientation, status, createdAt } = useMessageContext();

const { t } = useI18n();

const canRetry = computed(() => !hasOneDayPassed(createdAt.value));

// O motivo explicado (ex.: "131042 — A Meta recusou o envio...") é longo:
// além do hover, um clique fixa o balão aberto (toque no celular não tem hover).
const isPinned = ref(false);
const togglePinned = () => {
  isPinned.value = !isPinned.value;
};
</script>

<template>
  <div class="text-xs text-n-ruby-11 flex items-center gap-1.5">
    <div class="relative group flex items-center gap-1.5">
      <button
        type="button"
        class="flex items-center gap-1.5 cursor-pointer"
        :aria-expanded="isPinned"
        :aria-label="error"
        @click="togglePinned"
      >
        <span>{{ t('CHAT_LIST.FAILED_TO_SEND') }}</span>
        <span class="bg-n-alpha-2 rounded-md size-5 grid place-content-center">
          <Icon
            icon="i-lucide-alert-triangle"
            class="text-n-ruby-11 size-[14px]"
          />
        </span>
      </button>
      <div
        data-test-id="message-error-detail"
        class="absolute z-10 bg-n-alpha-3 px-4 py-3 border rounded-xl border-n-strong text-n-slate-12 bottom-6 w-80 max-w-[80vw] text-xs leading-relaxed whitespace-pre-line backdrop-blur-[100px] shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] group-hover:opacity-100 group-hover:visible transition-all break-words"
        :class="{
          'opacity-100 visible': isPinned,
          'opacity-0 invisible': !isPinned,
          'ltr:left-0 rtl:right-0': orientation === ORIENTATION.LEFT,
          'ltr:right-0 rtl:left-0': orientation === ORIENTATION.RIGHT,
        }"
      >
        {{ error }}
      </div>
    </div>
    <button
      v-if="canRetry"
      type="button"
      :disabled="status !== MESSAGE_STATUS.FAILED"
      class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      @click="emit('retry')"
    >
      <Icon icon="i-lucide-refresh-ccw" class="text-n-ruby-11 size-[14px]" />
    </button>
  </div>
</template>
